/**
 * Patients Async Thunks
 * All async actions for patient operations
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import { queryClient } from '@/query/queryClient';
import * as patientsApi from '../api/patientsApi';

/**
 * Fetch paginated patients with filters
 */
export const fetchPatients = createAsyncThunk(
  'patients/fetchPatients',
  async (filters, { rejectWithValue }) => {
    try {
      const data = await patientsApi.fetchPatientsWithPagination(filters);
      return {
        patients: data.users || [],
        total: data.total || 0,
      };
    } catch (error) {
      console.error('Error fetching patients:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Fetch single patient profile
 */
export const fetchPatientProfile = createAsyncThunk(
  'patients/fetchPatientProfile',
  async (customerId, { rejectWithValue }) => {
    try {
      const patient = await patientsApi.fetchPatientProfile(customerId);
      return { customerId, patient };
    } catch (error) {
      console.error('Error fetching patient profile:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Update patient profile
 */
export const updatePatientProfile = createAsyncThunk(
  'patients/updatePatientProfile',
  async ({ patientId, data }, { rejectWithValue }) => {
    try {
      const response = await patientsApi.updatePatientProfile(patientId, data);
      const updatedPatient = response?.data?.data || response?.data || response;
      queryClient.invalidateQueries({ queryKey: ['patient-search'] });
      return { patientId, patient: updatedPatient };
    } catch (error) {
      console.error('Error updating patient profile:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Update patient last opened (fire and forget)
 */
export const updatePatientLastOpened = createAsyncThunk(
  'patients/updatePatientLastOpened',
  async (patientId, { rejectWithValue }) => {
    try {
      await patientsApi.updatePatientLastOpened(patientId);
      return { patientId };
    } catch (error) {
      // Silently fail - this is non-critical
      console.error('Error updating last opened:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
